/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import React, { Component, ReactComponentElement } from 'react';
import { isMobile } from 'react-device-detect';

// models and services
import { AccountModel } from 'src/types/account.model';
import { FullUserModel } from 'src/types/user.model';
import * as Snackbar from 'src/bw/libs/snackbar';
import AuthService from '../../services/auth.service';
import LiteralsService from '../../services/literals/LiteralsService';
import MixpanelService from '../../services/mixpanel.service';
import PlaidService, { PlaidMetaData } from '../../services/plaid.service';

// components
import { ButtonPrimary, GlyphRight } from '@americanexpress/dls-react';
import { PRODUCT_NAME } from 'src/utils/constants';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import './PlaidButton.scss';
import {
    PlaidLink,
    PlaidLinkError,
    PlaidLinkOnEvent,
    PlaidLinkOnEventMetadata,
    PlaidLinkOnExit,
    PlaidLinkOnExitMetadata,
    PlaidLinkOnSuccess,
    PlaidLinkOnSuccessMetadata,
    PlaidLinkStableEvent
} from 'react-plaid-link';
import { DISABLE_PLAID } from 'src/config';

export default class PlaidButton extends Component<any> {
    public override state = {
        loaded: false,
        token: null as string
    };

    private async getNewToken(): Promise<void> {
        const token: string = await PlaidService.createLinkToken();
        this.setState({ token });
    }

    public override async componentDidMount(): Promise<void> {
        if (!this.props.user.temporary) await this.getNewToken();
    }

    public override render(): ReactComponentElement<any> {
        const className: string = this.props.className ? this.props.className : 'plaid-button';
        const icon = this.props.className === 'plaid-link' ? <GlyphRight className="right-arrow link-svg" /> : null;
        const paragraph =
            this.props.className === 'plaid-link' ? (
                <Typography variant={TypographyVariants.SUBTEXT}>
                    {LiteralsService.getAppName()} works best with connected accounts.
                </Typography>
            ) : null;

        let button: JSX.Element;

        button = !DISABLE_PLAID ? (
            <PlaidLink
                className={className}
                onSuccess={this.onSuccess}
                token={this.state.token}
                onEvent={this.onEvent}
                onExit={this.onExit}
            >
                Connect account
                {icon}
            </PlaidLink>
        ) : (
            <button disabled={true} className={className}>
                Syncing new accounts is currently disabled
                {icon}
            </button>
        );

        button = this.props.user.temporary ? (
            <div className="guest-login">
                <Typography variant={TypographyVariants.CAPTION} className="label">
                    Ready to use your own data?
                </Typography>
                <ButtonPrimary id="save" onClick={async () => await AuthService.logoutUser()} key="save">
                    Sign up for {PRODUCT_NAME}
                </ButtonPrimary>
            </div>
        ) : (
            button
        );

        return (
            <div className={this.props.noMargin ? 'plaid noMargin' : 'plaid'} key="plaid">
                {paragraph}
                {button}
            </div>
        );
    }

    onSuccess: PlaidLinkOnSuccess = async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
        let firstImport = true;
        if (_.get(this, 'props.user.institutions.length')) {
            firstImport = false;
        }

        if (firstImport && !isMobile) {
            this.props.handleFirstImport();
        }

        try {
            if (this.props.showLoader && !isMobile)
                this.props.showLoader([
                    LiteralsService.getLiteral('Loader.accountLine1'),
                    LiteralsService.getLiteral('Loader.accountLine2')
                ]);
            await PlaidService.addInstitution(publicToken, metadata.institution.institution_id);

            const user: FullUserModel = await this.props.handleReloadUser();

            const numberOfAccounts: number = _.filter(user.accounts, (account: AccountModel) => {
                return account.institution.institutionId === metadata.institution.institution_id;
            }).length;

            MixpanelService.track('Add Institution', {
                'Number of Institutions': user.institutions.length,
                'Number of Accounts': numberOfAccounts
            });

            if (!this.props.user.attributes.startupStep) {
                this.props.handleAccounts();
            }

            if (this.props.handleSuccess) {
                await this.props.handleSuccess();
            }
        } catch (err) {
            Snackbar.show({ text: LiteralsService.getLiteral('addAccountDrawer.syncError') });
        } finally {
            this.props.handleClearFirstImport();
            if (this.props.hideLoader) this.props.hideLoader();
        }
    };

    onEvent: PlaidLinkOnEvent = (eventName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
        // eventName = "TRANSITION_VIEW"
        // metadata  = {
        //   link_session_id: "1dc21685c-192e-4969-b4e9-bab890daae31",
        //   mfa_type:        "questions",
        //   timestamp:       "2017-09-14T14:42:19.350Z",
        //   view_name:       "MFA",
        // }
        if (eventName === PlaidLinkStableEvent.OPEN) {
            MixpanelService.track('Start Plaid');
        } else {
            MixpanelService.track('Plaid Event', { event: eventName, metadata });
            if (eventName === PlaidLinkStableEvent.EXIT || eventName === PlaidLinkStableEvent.ERROR) {
                void PlaidService.postUserLog(eventName, metadata as PlaidMetaData);
            }
        }
    };

    onExit: PlaidLinkOnExit = async (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
        MixpanelService.track('Plaid Exit', { error, metadata });
        if (error?.error_code === 'INVALID_LINK_TOKEN') {
            await this.getNewToken();
        }

        if (this.props.handleExit) {
            this.props.handleExit();
        }
    };
}
